// ANGEL FLIGHT — RenderPipeline: the runtime-swappable post-processing composer.
//
// ONE composer that supports two looks over the same scene + camera, switchable
// at runtime (key T / the touch FLIR button / the pause toggle). This is the
// SINGLE thermal implementation — the old parallel thermal-world stack
// (ThermalPipeline/WorldView3D/EnemyView) was unreachable and was deleted; the
// FLIR look is a POST MODE over the live colour world:
//
//   'color'   (default) — RenderPass → ColorGrade EffectPass (Bloom on genuine
//                          highlights + subtle vignette + light grain + mild CA).
//                          Renderer tone-maps ACESFilmic so the lit 3D world reads
//                          as a cohesive cinematic frame. NO FLIR LUT.
//   'thermal'           — RenderPass → BloomEffect + ThermalEffect (the FLIR
//                          gun-camera: luminance→LUT + grain/scanline/vignette).
//
// The RenderPass is built once and kept; only the trailing EffectPass (and the
// effects it owns) is rebuilt + swapped on setMode(). An HDR HalfFloat target is
// used where supported (so authored emissive heat / bright highlights exceed 1.0
// and survive into bloom + the thermal clip), with an UnsignedByte fallback.

import {
  BloomEffect,
  ClearPass,
  EffectComposer,
  EffectPass,
  RenderPass,
} from 'postprocessing';
import * as THREE from 'three';
import { RENDER } from '../config/render';
import { THERMAL } from '../config/thermal';
import { ColorGrade } from './ColorGrade';
import { ThermalEffect } from './ThermalEffect';

export type RenderMode = 'color' | 'thermal';

export class RenderPipeline {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly camera: THREE.Camera;
  private readonly composer: EffectComposer;
  private readonly renderPass: RenderPass;

  // Tone-mapping state to restore when leaving color mode (thermal keeps the
  // renderer's original behaviour, set by whoever owns the renderer).
  private readonly baseToneMapping: THREE.ToneMapping;
  private readonly baseToneMappingExposure: number;

  // The currently-active trailing EffectPass + whichever effect bundle it owns.
  // Exactly one of (grade) / (thermal) is non-null, matching `mode`.
  private effectPass: EffectPass;
  private grade: ColorGrade | null = null;
  /** The live ThermalEffect when in thermal mode (so callers can flip polarity). */
  thermal: ThermalEffect | null = null;

  // The gun-viewmodel overlay scene (set via setOverlay). In COLOR mode the
  // owner draws it directly to screen AFTER the composer (ungraded, the shipped
  // look). In THERMAL mode it must be part of the SENSOR FEED, so it is drawn
  // INTO the composer buffer (depth-only clear, no colour clear) before the
  // FLIR pass — the whole frame, gun included, goes through the LUT.
  private overlayScene: THREE.Scene | null = null;
  private overlayCamera: THREE.Camera | null = null;
  private overlayClear: ClearPass | null = null;
  private overlayPass: RenderPass | null = null;

  private mode: RenderMode;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    mode: RenderMode,
  ) {
    this.renderer = renderer;
    this.camera = camera;
    this.mode = mode;

    this.baseToneMapping = renderer.toneMapping;
    this.baseToneMappingExposure = renderer.toneMappingExposure;

    // Prefer a half-float HDR buffer so bright highlights / emissive heat > 1.0
    // survive into bloom + any clip; fall back to unsigned byte on GPUs without
    // float-render support.
    const caps = renderer.capabilities;
    const frameBufferType =
      caps.isWebGL2 || renderer.extensions.has('OES_texture_half_float')
        ? THREE.HalfFloatType
        : THREE.UnsignedByteType;

    this.composer = new EffectComposer(renderer, { frameBufferType });
    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    this.effectPass = this.buildEffectPass(mode);
    this.composer.addPass(this.effectPass);
    this.applyToneMapping(mode);
  }

  getMode(): RenderMode {
    return this.mode;
  }

  /** Register the gun-viewmodel overlay so THERMAL mode can pull it into the
   *  sensor feed (colour mode keeps drawing it after the composer). */
  setOverlay(scene: THREE.Scene, camera: THREE.Camera): void {
    this.overlayScene = scene;
    this.overlayCamera = camera;
    if (this.mode === 'thermal') this.rebuildChain(this.mode);
  }

  /** True while the composer renders the overlay itself (thermal feed) — the
   *  owner must NOT also draw the gun on top. */
  get ownsOverlay(): boolean {
    return this.overlayPass !== null;
  }

  /** Swap the look at runtime: rebuild the trailing chain, keep the RenderPass. */
  setMode(mode: RenderMode): void {
    if (mode === this.mode) return;
    this.rebuildChain(mode);
    this.mode = mode;
    this.applyToneMapping(mode);
  }

  /** Tear down + rebuild everything after the world RenderPass for `mode`:
   *  thermal inserts [depth-clear → gun overlay] before its EffectPass. */
  private rebuildChain(mode: RenderMode): void {
    this.composer.removePass(this.effectPass);
    this.disposeEffectPass();
    if (this.overlayPass) {
      this.composer.removePass(this.overlayPass);
      this.overlayPass.dispose();
      this.overlayPass = null;
    }
    if (this.overlayClear) {
      this.composer.removePass(this.overlayClear);
      this.overlayClear.dispose();
      this.overlayClear = null;
    }

    if (mode === 'thermal' && this.overlayScene && this.overlayCamera) {
      // Depth-only clear so the gun draws over the world without clipping it,
      // exactly like the direct overlay draw — but INSIDE the sensor feed.
      this.overlayClear = new ClearPass(false, true, false);
      this.overlayPass = new RenderPass(this.overlayScene, this.overlayCamera);
      this.overlayPass.clearPass.enabled = false; // keep the world colour
      this.overlayPass.ignoreBackground = true;
      this.composer.addPass(this.overlayClear);
      this.composer.addPass(this.overlayPass);
    }

    this.effectPass = this.buildEffectPass(mode);
    this.composer.addPass(this.effectPass);
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  /** Render one frame through the active chain. deltaSec advances grain/jitter. */
  render(deltaSec: number): void {
    this.composer.render(deltaSec);
  }

  dispose(): void {
    this.disposeEffectPass();
    this.overlayPass?.dispose();
    this.overlayClear?.dispose();
    this.composer.dispose();
    // Leave the renderer's tone mapping as the caller's original baseline.
    this.renderer.toneMapping = this.baseToneMapping;
    this.renderer.toneMappingExposure = this.baseToneMappingExposure;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** Build the trailing EffectPass for a mode, wiring `grade`/`thermal` state. */
  private buildEffectPass(mode: RenderMode): EffectPass {
    if (mode === 'color') {
      const grade = new ColorGrade();
      this.grade = grade;
      this.thermal = null;
      return new EffectPass(this.camera, ...grade.effects);
    }

    // thermal — the FLIR chain: bloom on the hot tail, then the LUT pass.
    const b = THERMAL.bloom;
    const bloom = new BloomEffect({
      intensity: b.intensity,
      luminanceThreshold: b.luminanceThreshold,
      luminanceSmoothing: b.luminanceSmoothing,
      radius: b.radius,
      mipmapBlur: b.mipmapBlur,
    });
    const thermal = new ThermalEffect();
    this.thermal = thermal;
    this.grade = null;
    // Bloom (hot halos) then Thermal (FLIR map) fused into one EffectPass.
    return new EffectPass(this.camera, bloom, thermal);
  }

  /** Dispose the current trailing pass + whichever effect bundle it owns. */
  private disposeEffectPass(): void {
    this.effectPass.dispose();
    if (this.grade) {
      this.grade.dispose();
      this.grade = null;
    }
    if (this.thermal) {
      this.thermal.dispose();
      this.thermal = null;
    }
  }

  /** Color mode: ACESFilmic + the graded exposure. Thermal: caller's baseline. */
  private applyToneMapping(mode: RenderMode): void {
    if (mode === 'color') {
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = RENDER.grade.toneMappingExposure;
    } else {
      this.renderer.toneMapping = this.baseToneMapping;
      this.renderer.toneMappingExposure = this.baseToneMappingExposure;
    }
  }
}
